import { useEffect, useState } from "react";
import { apiGet, apiPost } from "../../lib/apiClient";
import Ke2027Detail from "./Ke2027Detail";
import FootballDetail from "./FootballDetail";

const label = "mb-1 block text-xs font-medium text-muted-foreground";

/**
 * Where a market goes, and what that means. Ported from the woza-tickets prototype's
 * PlacementPicker.jsx: a market can sit in more than one cluster (checkboxes, not a
 * select — a question about the shilling during an election belongs in Kenya 2027 *and*
 * Economy), each with its own sub-tags. The "where this will be seen" preview comes from
 * the server (previewPath) rather than being recomputed here, so it can never drift from
 * the real placement rule; if that call fails the panel falls back to the chosen
 * clusters' own declared surfaces rather than going blank.
 *
 * previewPath/ke2027RefPath/createPartyPath let this one component serve both the admin
 * portal (/admin/...) and the partner portal (/portal/...) call sites without depending
 * on which app it's running in.
 */
export default function PlacementPicker({
  clusters,
  clusterIds,
  tagIds,
  county,
  constituency,
  partyIds,
  competitionId,
  homeTeamId,
  awayTeamId,
  kickoffAt,
  teamIds,
  visibility,
  onChange,
  previewPath,
  ke2027RefPath,
  createPartyPath,
  footballRefPath,
  createTeamPath,
  error,
  suggestions,
}) {
  const [placement, setPlacement] = useState(null);
  const [ke2027Ref, setKe2027Ref] = useState(null);
  const [footballRef, setFootballRef] = useState(null);

  const chosen = (clusters || []).filter((c) => clusterIds.includes(c.id));
  const hasKe2027 = chosen.some((c) => c.keyName === "ke2027");
  const hasFootball = chosen.some((c) => c.keyName === "football");

  useEffect(() => {
    if (!hasKe2027 || ke2027Ref) return;
    apiGet(ke2027RefPath)
      .then(setKe2027Ref)
      .catch(() => setKe2027Ref({ counties: [], constituencies: [], blocs: [], parties: [], races: [] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasKe2027]);

  useEffect(() => {
    if (!hasFootball || footballRef) return;
    apiGet(footballRefPath)
      .then(setFootballRef)
      .catch(() => setFootballRef({ competitions: [], teams: [], kinds: [] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasFootball]);

  // Un-checking ke2027 drops its unit/parties with it — leaving them behind would send
  // the server county/constituency/partyIds with no ke2027 cluster chosen, which
  // resolvePlacement simply ignores, but the form should reflect what will actually be
  // submitted.
  useEffect(() => {
    if (!hasKe2027 && (county || constituency || partyIds.length)) {
      onChange({ county: null, constituency: null, partyIds: [] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasKe2027]);

  // Same idea for football: unticking it must not leave a stale competition/fixture
  // behind for resolvePlacement to ignore while the form still shows it as set.
  useEffect(() => {
    if (!hasFootball && (competitionId || homeTeamId || awayTeamId || kickoffAt || teamIds.length)) {
      onChange({ competitionId: null, homeTeamId: null, awayTeamId: null, kickoffAt: null, teamIds: [] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasFootball]);

  useEffect(() => {
    let cancelled = false;
    if (!clusterIds.length) {
      setPlacement(null);
      return undefined;
    }
    const t = setTimeout(async () => {
      try {
        const p = await apiPost(previewPath, {
          clusterIds,
          tagIds,
          visibility,
          county: county || null,
          constituency: constituency || null,
          partyIds,
          competitionId: competitionId || null,
          homeTeamId: homeTeamId || null,
          awayTeamId: awayTeamId || null,
          teamIds,
        });
        if (!cancelled) setPlacement(p);
      } catch {
        if (!cancelled) {
          setPlacement({
            surfaces: [...new Set(chosen.flatMap((c) => c.surfaces))],
            byCluster: chosen.map((c) => ({ clusterId: c.id, clusterName: c.label, surfaces: c.surfaces })),
            note: null,
            degraded: true,
          });
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, 180);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    clusterIds.join(","),
    tagIds.join(","),
    visibility,
    county,
    constituency,
    partyIds.join(","),
    competitionId,
    homeTeamId,
    awayTeamId,
    teamIds.join(","),
  ]);

  function toggleCluster(cluster) {
    if (clusterIds.includes(cluster.id)) {
      const ownTags = new Set(cluster.tags.map((t) => t.id));
      onChange({ clusterIds: clusterIds.filter((id) => id !== cluster.id), tagIds: tagIds.filter((id) => !ownTags.has(id)) });
    } else {
      onChange({ clusterIds: [...clusterIds, cluster.id], tagIds });
    }
  }

  function toggleTag(id) {
    onChange({ clusterIds, tagIds: tagIds.includes(id) ? tagIds.filter((t) => t !== id) : [...tagIds, id] });
  }

  async function createParty(partyInput) {
    const res = await apiPost(createPartyPath, partyInput);
    onChange({ partyIds: [...new Set([...partyIds, res.party.id])] });
    return res;
  }

  async function createTeam(teamInput) {
    const res = await apiPost(createTeamPath, teamInput);
    return res;
  }

  return (
    <div className="rounded-xl border border-gray-200 p-4 dark:border-white/10">
      <p className={label}>Placement</p>
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
        Clusters decide where this market is shown. Pick as many as genuinely fit — a market can live in more than one — then use the sub-tags to place it precisely inside each.
      </p>

      {(suggestions || []).length > 0 && (
        <div className="mt-3 rounded-xl border border-dashed border-brand-300 bg-brand-50/40 p-3 dark:border-brand-400/30 dark:bg-brand-400/5">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-brand-600 dark:text-brand-300">
            Looks like it belongs in
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {suggestions
              .filter((s) => s.clusterId != null)
              .map((s) => {
                const on = clusterIds.includes(s.clusterId);
                const cluster = (clusters || []).find((c) => c.id === s.clusterId);
                return (
                  <button
                    key={s.clusterKey}
                    type="button"
                    disabled={on || !cluster}
                    onClick={() => cluster && toggleCluster(cluster)}
                    title={`Matched: ${s.matched.join(", ")}`}
                    className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
                      on
                        ? "border-brand-400 bg-brand-500/15 text-brand-700 dark:text-brand-300"
                        : "border-gray-300 bg-white text-gray-700 hover:border-brand-400 hover:text-brand-600 dark:border-white/20 dark:bg-transparent dark:text-gray-200"
                    }`}
                  >
                    {s.label}
                    {on ? " ✓" : ""}
                  </button>
                );
              })}
          </div>
          <p className="mt-2 text-[11px] text-gray-500 dark:text-gray-400">
            Suggested from the words in your question. Add the ones that fit; ignore the rest.
          </p>
        </div>
      )}

      <div className="mt-4 space-y-2">
        {(clusters || []).map((cluster) => {
          const on = clusterIds.includes(cluster.id);
          const tagsOn = cluster.tags.filter((t) => tagIds.includes(t.id));
          return (
            <div key={cluster.id} className={`rounded-xl border transition ${on ? "border-brand-400 bg-brand-50/50 dark:bg-brand-400/5" : "border-gray-200 dark:border-white/10"}`}>
              <label className="flex cursor-pointer items-start gap-3 p-3">
                <input type="checkbox" className="mt-1" checked={on} onChange={() => toggleCluster(cluster)} />
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold text-navy-700 dark:text-white">{cluster.label}</span>
                    {cluster.kind === "MODULE" && (
                      <span className="rounded-full bg-brand-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-brand-600 dark:text-brand-300">Module</span>
                    )}
                    {on && tagsOn.length > 0 && (
                      <span className="text-[11px] text-gray-500 dark:text-gray-400">{tagsOn.length} tag{tagsOn.length === 1 ? "" : "s"}</span>
                    )}
                  </span>
                  {cluster.blurb && <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">{cluster.blurb}</span>}
                </span>
              </label>

              {on && cluster.tags?.length > 0 && (
                <div className="border-t border-gray-200 px-3 pb-3 pt-3 dark:border-white/10">
                  <p className="text-xs font-medium text-navy-700 dark:text-white">{cluster.tagLabel || "Sub-tags"}</p>
                  {cluster.tagHint && <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{cluster.tagHint}</p>}
                  <div className="mt-2 flex flex-wrap gap-2">
                    {cluster.tags.map((t) => {
                      const tagOn = tagIds.includes(t.id);
                      return (
                        <button
                          key={t.id}
                          type="button"
                          aria-pressed={tagOn}
                          onClick={() => toggleTag(t.id)}
                          className={`rounded-full px-3 py-1.5 text-xs font-medium transition ${
                            tagOn ? "bg-primary text-primary-foreground" : "border border-gray-200 bg-white text-navy-700 hover:bg-gray-50 dark:border-white/10 dark:bg-transparent dark:text-white dark:hover:bg-white/5"
                          }`}
                        >
                          {t.name}
                        </button>
                      );
                    })}
                  </div>

                  {cluster.keyName === "ke2027" && ke2027Ref && (
                    <Ke2027Detail
                      races={cluster.tags}
                      tagIds={tagIds}
                      counties={ke2027Ref.counties || []}
                      constituencies={ke2027Ref.constituencies || []}
                      parties={ke2027Ref.parties || []}
                      blocs={ke2027Ref.blocs || []}
                      county={county}
                      constituency={constituency}
                      partyIds={partyIds}
                      onChange={onChange}
                      onCreateParty={createParty}
                    />
                  )}

                  {cluster.keyName === "football" && footballRef && (
                    <FootballDetail
                      kinds={cluster.tags}
                      tagIds={tagIds}
                      competitions={footballRef.competitions || []}
                      teams={footballRef.teams || []}
                      competitionId={competitionId}
                      homeTeamId={homeTeamId}
                      awayTeamId={awayTeamId}
                      onChange={onChange}
                      onCreateTeam={createTeam}
                    />
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {error && <p className="mt-2 text-xs font-medium text-red-500">{error}</p>}

      <div className="mt-4 rounded-xl bg-gray-50 p-4 dark:bg-white/5">
        <p className="text-xs font-medium text-muted-foreground">Where this will be seen</p>
        {!clusterIds.length ? (
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">Without a cluster this market is only reachable from All markets and from search.</p>
        ) : placement?.note ? (
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">{placement.note}</p>
        ) : placement?.byCluster?.length ? (
          <>
            <div className="mt-3 space-y-3">
              {placement.byCluster.map((group) => (
                <div key={group.clusterId}>
                  <p className="text-xs font-semibold text-navy-700 dark:text-white">{group.clusterName}</p>
                  <ul className="mt-1 space-y-1">
                    {group.surfaces.map((s) => (
                      <li key={s} className="flex items-start gap-2 text-sm text-navy-700 dark:text-white">
                        <span className="mt-0.5 text-green-600">✓</span>
                        {s}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            {placement.byCluster.length > 1 && (
              <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                {placement.surfaces.length} distinct place{placement.surfaces.length === 1 ? "" : "s"} in total — a surface shared by two clusters is only counted once.
              </p>
            )}
            {placement.mapped && (
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">One of these races is drawn on the county map, so the market is also reachable by tapping its county or constituency.</p>
            )}
            {placement.degraded && <p className="mt-2 text-xs text-amber-600">Showing each cluster's default surfaces — the live preview could not be reached.</p>}
          </>
        ) : (
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Working out placement…</p>
        )}
      </div>
    </div>
  );
}
